import { Guid } from '../../../store/model/guid';
import { AssertionException } from '../../../utils/log';
import { isQuiz, isSequencedFlowItem, Item, ItemHelper } from '@ael/core-api';
import * as Immutable from 'immutable';
import { StudentStore } from '../../../store/models/Store';
import { getItemByIdSelector } from '../../../store/reducers/items/itemsSelectors';
import { createSelector } from 'reselect';

/**
 * The read-only surface of {@link CompletedItemsStore}. Consumers that only ask questions take this
 * type so they cannot mutate a store instance that is shared between them via a memoized selector.
 */
export interface ReadonlyCompletedItemsStore {
  isInProgress: (item: Item) => boolean;
  isFinished: (item: Item) => boolean;
}

export class CompletedItemsStore implements ReadonlyCompletedItemsStore {
  constructor(private _completedItems: Immutable.Set<Guid>, private _getItemById: (itemId: Guid) => Item) {}

  public isInProgress = (item: Item): boolean => {
    return this._completedItems.contains(IdentifierFactory(item, false).serialize());
  };

  public isFinished = (item: Item): boolean => {
    return this._completedItems.contains(IdentifierFactory(item, true).serialize());
  };

  addStartedItem = (itemId: Guid) => {
    const item = this._getItemById(itemId);
    const trackingIdentifier = IdentifierFactory(item, false);
    this._completedItems = this._completedItems.add(trackingIdentifier.serialize());
  };

  addFinishedItem = (itemId: Guid) => {
    const item = this._getItemById(itemId);
    const trackingIdentifier = IdentifierFactory(item, true);
    this._completedItems = this._completedItems.add(trackingIdentifier.serialize());
  };

  removeFinishedItem = (itemId: Guid) => {
    const item = this._getItemById(itemId);
    const trackingIdentifier = IdentifierFactory(item, true);
    this._completedItems = this._completedItems.remove(trackingIdentifier.serialize());
  };

  reset = () => {
    this._completedItems = this._completedItems.clear();
  };

  toJS = (): Immutable.Set<string> => {
    return this._completedItems;
  };
}

const IdentifierFactory = (item: Item, isCompleted: boolean): IItemIdentifier => {
  if (ItemHelper.isContentItem(item)) {
    return new ContentItemIdentifier(item.id);
  }

  if (isQuiz(item) || isSequencedFlowItem(item)) {
    if (!isCompleted) {
      return new ItemIdentifier(item.id);
    }
    return new CompletedItemIdentifier(item.id);
  }

  throw AssertionException('Unknown item for tracking identifier');
};

interface IItemIdentifier {
  serialize: () => string;
}

class ContentItemIdentifier implements IItemIdentifier {
  protected _itemId: Guid;
  constructor(itemId: Guid) {
    this._itemId = itemId;
  }

  serialize() {
    return this._itemId;
  }
}

class ItemIdentifier implements IItemIdentifier {
  protected _itemId: Guid;
  constructor(itemId: Guid) {
    this._itemId = itemId;
  }

  serialize() {
    return this._itemId;
  }
}

/**
 * Creates a fresh, mutable store. Only for the tracking thunks, which mutate the returned instance
 * before serializing it. Everything that only reads must use {@link completedItemsStoreSelector},
 * whose result is shared and must therefore stay untouched.
 */
export const createCompletedItemsStoreFromState = (state: StudentStore) => {
  return new CompletedItemsStore(state.tracking.items.finishedItems, getItemByIdSelector(state));
};

const getFinishedItems = (state: StudentStore) => state.tracking.items.finishedItems;

export const completedItemsStoreSelector: (state: StudentStore) => ReadonlyCompletedItemsStore = createSelector(
  getFinishedItems,
  getItemByIdSelector,
  (finishedItems, getItemById): ReadonlyCompletedItemsStore => new CompletedItemsStore(finishedItems, getItemById)
);

export const completedSeqFlowPostfix = '-completed';

class CompletedItemIdentifier extends ItemIdentifier {
  serialize() {
    return this._itemId + completedSeqFlowPostfix;
  }
}
